import type { Answer, MarketComparison, MarketBenchmark, FinancialProjection, YearlyProjection } from '@/types';
import { getInvestmentRange, getBenchmark, getAverageBenchmark } from './market-data-loader';
import { generateInsightText } from '../scoring/insight-generator';
import { formatCurrency } from '@/utils/formatters';

export class BenchmarkService {
  private answers: Map<string, Answer>;
  private benchmark: MarketBenchmark | null = null;
  private segmento: string = '';
  private investmentValue: number = 0;

  constructor(answers: Answer[]) {
    this.answers = new Map(answers.map(a => [a.questionId, a]));
    this.initializeBenchmark();
  }

  private initializeBenchmark(): void {
    const segmentAnswer = this.answers.get('cred_segment');
    const investmentAnswer = this.answers.get('fin_investment');

    if (segmentAnswer) {
      this.segmento = segmentAnswer.value as string;
    }

    if (investmentAnswer) {
      this.investmentValue = investmentAnswer.value as number;
    }

    const investmentRange = getInvestmentRange(this.investmentValue);

    if (investmentRange) {
      this.benchmark = getBenchmark(investmentRange.id, this.segmento);
    }

    if (!this.benchmark) {
      this.benchmark = getAverageBenchmark(this.segmento);
    }
  }

  generateComparison(): MarketComparison[] {
    const comparisons: MarketComparison[] = [];

    if (!this.benchmark) {
      return comparisons;
    }

    const investmentRange = getInvestmentRange(this.investmentValue);
    if (this.investmentValue > 0 && investmentRange) {
      const marketAvgInvestment = investmentRange.max !== null
        ? Math.round((investmentRange.min + investmentRange.max) / 2)
        : Math.round(investmentRange.min * 1.5);

      const totalUserInvestment = this.investmentValue + this.calculateSuggestedFranchiseFee();
      const marketTotalInvestment = marketAvgInvestment + this.benchmark.taxa_franquia;

      const investmentInsight = totalUserInvestment <= marketTotalInvestment * 1.1
        ? (totalUserInvestment < marketTotalInvestment * 0.9 ? 'above_average' : 'within_expected')
        : 'below_average';

      comparisons.push({
        indicator: 'Investimento Total (Invest. + Taxa)',
        marketAverage: formatCurrency(marketTotalInvestment),
        companyValue: formatCurrency(totalUserInvestment),
        insight: investmentInsight,
        insightText: 'Investimento estimado + Taxa de Franquia sugerida'
      });
    }

    const grossRevenue = (this.answers.get('fin_gross_revenue')?.value as number) || 0;

    const revenueInsight = generateInsightText(
      'faturamento',
      this.benchmark.faturamento_medio_mensal,
      grossRevenue
    );

    comparisons.push({
      indicator: 'Faturamento Médio Mensal',
      marketAverage: formatCurrency(this.benchmark.faturamento_medio_mensal),
      companyValue: formatCurrency(grossRevenue),
      insight: revenueInsight.insight,
      insightText: revenueInsight.text
    });

    const netProfitRate = 1 - 0.15 - 0.20 - 0.10; // impostos 15% + custos fixos 20% + marketing 10%
    const marketNetProfit = this.benchmark.faturamento_medio_mensal * netProfitRate;
    const netRevenue = (this.answers.get('fin_net_revenue')?.value as number) || 0;
    const companyNetProfit = netRevenue > 0 ? netRevenue : grossRevenue * netProfitRate;

    const netProfitInsight = generateInsightText('faturamento', marketNetProfit, companyNetProfit);

    comparisons.push({
      indicator: 'Lucro Líquido Mensal Estimado',
      marketAverage: formatCurrency(marketNetProfit),
      companyValue: formatCurrency(companyNetProfit),
      insight: netProfitInsight.insight,
      insightText: 'Estimativa após impostos, custos fixos e marketing'
    });

    comparisons.push({
      indicator: 'Taxa de Franquia Média',
      marketAverage: formatCurrency(this.benchmark.taxa_franquia),
      companyValue: formatCurrency(this.calculateSuggestedFranchiseFee()),
      insight: 'within_expected',
      insightText: 'Valor sugerido com base no mercado'
    });

    comparisons.push({
      indicator: 'Royalties',
      marketAverage: `${(this.benchmark.royalties * 100).toFixed(1)}%`,
      companyValue: `${(this.benchmark.royalties * 100).toFixed(1)}%`,
      insight: 'within_expected',
      insightText: 'Valor sugerido com base no mercado'
    });

    comparisons.push({
      indicator: 'Payback Médio',
      marketAverage: `${this.benchmark.payback_meses} meses`,
      companyValue: `${this.calculateEstimatedPayback()} meses`,
      ...this.getPaybackInsight()
    });

    const marketGrowth = this.benchmark.vendas_mensais;
    comparisons.push({
      indicator: 'Média de Crescimento (unidades/ano)',
      marketAverage: String(marketGrowth),
      companyValue: String(Math.round(marketGrowth * 0.8)),
      insight: 'within_expected',
      insightText: 'Projeção conservadora baseada no mercado'
    });

    return comparisons;
  }

  private calculateSuggestedFranchiseFee(): number {
    if (this.benchmark) {
      return this.benchmark.taxa_franquia;
    }
    return this.investmentValue * 0.15;
  }

  private calculateEstimatedPayback(): number {
    const grossRevenue = (this.answers.get('fin_gross_revenue')?.value as number) || 0;
    const franchiseFee = this.calculateSuggestedFranchiseFee();
    const totalInvestment = this.investmentValue + franchiseFee;

    // Lucro líquido: usa fin_net_revenue informado ou estima com margem de 55%
    const netRevenue = (this.answers.get('fin_net_revenue')?.value as number) || 0;
    const netProfitRate = 1 - 0.15 - 0.20 - 0.10;
    const monthlyNetProfit = netRevenue > 0 ? netRevenue : grossRevenue * netProfitRate;

    if (monthlyNetProfit <= 0 || totalInvestment <= 0) {
      return this.benchmark?.payback_meses || 24;
    }

    const paybackMonths = Math.round(totalInvestment / monthlyNetProfit);
    return Math.max(paybackMonths, 1);
  }

  private getPaybackInsight(): { insight: 'above_average' | 'within_expected' | 'below_average'; insightText: string } {
    const estimatedPayback = this.calculateEstimatedPayback();
    const marketPayback = this.benchmark?.payback_meses || 24;

    if (estimatedPayback < marketPayback * 0.9) {
      return {
        insight: 'above_average',
        insightText: 'Retorno mais rápido que a média do mercado'
      };
    } else if (estimatedPayback <= marketPayback * 1.1) {
      return {
        insight: 'within_expected',
        insightText: 'Retorno dentro da média do mercado'
      };
    } else {
      return {
        insight: 'below_average',
        insightText: 'Retorno mais lento que a média do mercado'
      };
    }
  }

  generateFinancialProjection(): FinancialProjection {
    const growthRate = 0.25;
    const initialUnits = 3;
    const years = 5;

    const unitGrossRevenue = (this.answers.get('fin_gross_revenue')?.value as number) || 50000;

    const franchiseFee = this.calculateSuggestedFranchiseFee();
    const royaltiesRate = this.benchmark?.royalties || 0.06;

    const projections: YearlyProjection[] = [];
    const franchiseFeeRevenue: number[] = [];
    const royaltiesRevenue: number[] = [];

    let cumulativeUnits = initialUnits;
    let cumulativeNetProfit = 0;

    for (let year = 1; year <= years; year++) {
      const newUnits = Math.round(cumulativeUnits * growthRate);
      cumulativeUnits += newUnits;

      const yearFranchiseFeeRevenue = newUnits * franchiseFee;
      franchiseFeeRevenue.push(yearFranchiseFeeRevenue);

      const yearRoyaltiesRevenue = cumulativeUnits * unitGrossRevenue * 12 * royaltiesRate;
      royaltiesRevenue.push(yearRoyaltiesRevenue);

      const totalGrossRevenue = yearFranchiseFeeRevenue + yearRoyaltiesRevenue;
      const taxes = totalGrossRevenue * 0.15;
      const grossProfit = totalGrossRevenue - taxes;
      const fixedCosts = totalGrossRevenue * 0.20;
      const marketingInvestment = totalGrossRevenue * 0.10;
      const netProfit = grossProfit - fixedCosts - marketingInvestment;
      const profitabilityPercentage = totalGrossRevenue > 0
        ? (netProfit / totalGrossRevenue) * 100
        : 0;

      cumulativeNetProfit += netProfit;

      projections.push({
        year,
        unitsCount: cumulativeUnits,
        grossRevenue: totalGrossRevenue,
        taxes,
        grossProfit,
        fixedCosts,
        marketingInvestment,
        netProfit,
        profitabilityPercentage,
        cumulativeNetProfit
      });
    }

    return {
      growthRate,
      initialUnits,
      projections,
      franchiseFeeRevenue,
      royaltiesRevenue,
      averageRepresentation: {
        taxes: 15,
        fixedCosts: 20,
        marketing: 10
      }
    };
  }

  getSegment(): string {
    return this.segmento;
  }

  getInvestmentValue(): number {
    return this.investmentValue;
  }

  getBenchmarkData(): MarketBenchmark | null {
    return this.benchmark;
  }
}
